import React from 'react'
import type { Property } from 'csstype'

import { DEFAULT_LIGHT_CONTRAST_COLOR, findContrastColor } from 'services/color'
import { formatCentsToReais } from 'services/money'
import { classNames } from 'services/string'

import PlanBenefits from 'components/Plans/PlanBenefits'
import { planBenefits } from 'components/Plans/constants'
import ContactButton from 'components/ContactButton'

interface PlanCardProps extends Omit<Queries.PlansContentFragment, 'id'> {
  className?: string
  name: string
  description: string
  metaDescription: string
  primaryColor?: Property.Color
  secondaryColor?: Property.Color
  blurred?: boolean
  redirectToTarget?: boolean
  planBenefitsTitle?: string
  customPlanBenefits?: string[]
  offerPlan?: boolean
  offerDiscount?: number
}

const PlanCard = ({
  className,
  name,
  metaDescription,
  monthly,
  value,
  valueFrom,
  primaryColor,
  secondaryColor,
  planBenefitsTitle,
  customPlanBenefits,
  redirectToTarget,
  offerDiscount,
  offerPlan = false,
  blurred = false,
}: PlanCardProps) => {
  const primaryContrastColor = findContrastColor(primaryColor || 'var(--color-primary-600)')
  const secondaryContrastColor = findContrastColor(secondaryColor || 'var(--color-primary-100)')

  const discountLabel = typeof offerDiscount === 'number' && offerDiscount > 0 ? `${Math.round(offerDiscount * 100)}% OFF` : 'Oferta'

  return (
    <div
      className={classNames(
        className,
        'flex flex-col justify-between gap-4 rounded-xl border-t-2 border-l-2 p-4 shadow-lg drop-shadow-lg',
        blurred && 'backdrop-blur-md',
        offerPlan ? 'shadow-lg drop-shadow-lg bg-black text-white' : 'shadow-lg drop-shadow-lg',
      )}
      style={{
        backgroundColor: offerPlan ? undefined : `${secondaryColor || 'var(--color-primary-100)'}${blurred ? '80' : ''}`,
        color: offerPlan ? undefined : secondaryContrastColor,
        borderColor: offerPlan ? 'var(--color-primary-600)' : primaryColor || 'var(--color-primary-600)',
      }}
    >
      <div className={classNames('flex justify-end items-start', offerPlan ? 'min-h-[22px]' : undefined)}>
        {offerPlan && valueFrom && (
          <div className='px-2 py-0.5 rounded-full bg-red-500 shadow leading-none'>
            <span className='text-white font-semibold uppercase text-[12px] tracking-wide'>{discountLabel}</span>
          </div>
        )}
      </div>

      <div className='flex flex-col gap-3'>
        <p
          className={classNames('font-semibold', offerPlan ? 'text-primary-600' : undefined)}
          style={offerPlan ? undefined : { color: primaryColor || 'var(--color-primary-600)' }}
        >
          {name}
        </p>

        <div className='flex flex-col gap-2 justify-end h-auto md:h-24'>
          {Boolean(valueFrom) && (
            <div>
              <p className='leading-none font-normal opacity-70'>
                <span className='text-[0.6rem]'>De</span>
                <br />
                <span className='line-through'>{formatCentsToReais(valueFrom || 0)}</span>
                {monthly && <span className='text-xs no-underline font-normal'>/mês</span>}
              </p>
            </div>
          )}

          <div>
            <p className='text-xs'>A partir de *</p>
            <p className='text-4xl font-semibold'>
              {formatCentsToReais(value || 0)}
              {monthly && <span className='text-sm font-normal'>/mês</span>}
            </p>
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-4'>
        <hr
          className='border-none h-px'
          style={{ backgroundColor: offerPlan ? 'var(--color-primary-600)' : primaryColor || 'var(--color-primary-600)' }}
        />

        <PlanBenefits
          title={planBenefitsTitle || 'Disponível no plano:'}
          benefits={customPlanBenefits || planBenefits}
          planName={name}
          dark={offerPlan ? true : secondaryContrastColor !== DEFAULT_LIGHT_CONTRAST_COLOR}
        />

        <p className='text-right text-[0.7rem] font-light'>* Consulte condições</p>

        {!offerPlan && (
          <div className='w-full'>
            <ContactButton
              redirectToTarget={redirectToTarget}
              componentName='PlanCard'
              description={metaDescription}
              label='Entre em contato'
              targetId='contact-section'
              className='w-full'
              colorSchema='styled'
              style={{
                backgroundColor: primaryColor || 'var(--color-primary-600)',
                color: primaryContrastColor,
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default PlanCard
